<template>
  <div class="icon-showcase-container">
    <div class="page-header">
      <h1 class="page-title">圖示展示與選擇器測試</h1>
      <p class="page-subtitle">共 {{ allIcons.length }} 個 RemixIcon 圖示可供選擇</p>
    </div>

    <!-- 測試區域 -->
    <div class="test-section">
      <h2 class="section-title">IconPicker 元件測試</h2>
      <div class="test-card">
        <div class="form-group">
          <label>選擇圖示：</label>
          <IconPicker
            v-model="selectedIcon"
            :disabled="false"
          />
        </div>
        
        <div class="result-display" v-if="selectedIcon">
          <h3>已選擇的圖示：</h3>
          <div class="selected-icon-preview">
            <i :class="selectedIcon" class="large-icon"></i>
            <p>{{ selectedIcon }}</p>
          </div>
          
          <div class="code-example">
            <h4>使用範例：</h4>
            <pre><code>&lt;i class="{{ selectedIcon }}"&gt;&lt;/i&gt;</code></pre>
          </div>
        </div>
      </div>
    </div>

    <!-- 所有圖示展示 -->
    <div class="showcase-section">
      <h2 class="section-title">所有可用圖示</h2>
      
      <!-- 搜尋框 -->
      <div class="search-bar">
        <i class="ri-search-line"></i>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="搜尋圖示..."
        />
        <button v-if="searchQuery" @click="searchQuery = ''" class="btn-clear-search">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <!-- 分類篩選 -->
      <div class="category-filters">
        <button
          v-for="cat in categories"
          :key="cat.key"
          :class="['category-btn', { active: selectedCategory === cat.key }]"
          @click="selectedCategory = cat.key"
        >
          <i :class="cat.icon"></i>
          {{ cat.label }}
          <span class="count">({{ cat.count }})</span>
        </button>
      </div>

      <!-- 圖示網格 -->
      <div class="icons-grid">
        <div
          v-for="icon in filteredIcons"
          :key="icon.name"
          class="icon-card"
          :class="{ selected: selectedIcon === icon.name }"
          @click="selectIconFromShowcase(icon.name)"
          :title="icon.name"
        >
          <i :class="icon.name"></i>
          <span class="icon-label">{{ icon.displayName }}</span>
        </div>
      </div>

      <div v-if="filteredIcons.length === 0" class="empty-state">
        <i class="ri-inbox-line"></i>
        <p>沒有找到符合 "{{ searchQuery }}" 的圖示</p>
      </div>

      <!-- 統計資訊 -->
      <div class="stats-info">
        <p>顯示 {{ filteredIcons.length }} / {{ allIcons.length }} 個圖示</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import IconPicker from '@/components/IconPicker.vue';
import { getCatalogIconEntries, getIconCategoryFilters } from '@/constants/iconCatalog.js';
import { showSuccess } from '@/utils/swal.js';

export default {
  name: 'IconShowcase',
  components: {
    IconPicker
  },
  setup() {
    const selectedIcon = ref('');
    const searchQuery = ref('');
    const selectedCategory = ref('all');

    const allIcons = getCatalogIconEntries();
    const categories = computed(() => getIconCategoryFilters());

    // 篩選圖示
    const filteredIcons = computed(() => {
      let icons = allIcons;
      
      // 分類篩選
      if (selectedCategory.value !== 'all') {
        icons = icons.filter(icon => icon.category === selectedCategory.value);
      }
      
      // 搜尋篩選
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        icons = icons.filter(icon => 
          icon.name.toLowerCase().includes(query) ||
          icon.displayName.toLowerCase().includes(query)
        );
      }
      
      return icons;
    });

    const selectIconFromShowcase = async (iconName) => {
      selectedIcon.value = iconName;
      await showSuccess('已選擇', `圖示：${iconName}`);
    };

    return {
      selectedIcon,
      searchQuery,
      selectedCategory,
      allIcons,
      categories,
      filteredIcons,
      selectIconFromShowcase
    };
  }
};
</script>

<style scoped>
.icon-showcase-container {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 32px;
  text-align: center;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
}

/* 測試區域 */
.test-section {
  margin-bottom: 48px;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #007bff;
}

.test-card {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 24px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 8px;
}

.result-display {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

.result-display h3 {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
}

.selected-icon-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
}

.large-icon {
  font-size: 64px;
  color: #007bff;
}

.selected-icon-preview p {
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #666;
  margin: 0;
}

.code-example {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  border-left: 4px solid #007bff;
}

.code-example h4 {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.code-example pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #333;
}

/* 展示區域 */
.showcase-section {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.search-bar {
  position: relative;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.search-bar i {
  font-size: 20px;
  color: #666;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  outline: none;
}

.btn-clear-search {
  padding: 4px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #666;
  transition: color 0.2s;
}

.btn-clear-search:hover {
  color: #333;
}

.category-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.category-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid #e0e0e0;
  background: white;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.category-btn:hover {
  border-color: #007bff;
  background: #f0f7ff;
}

.category-btn.active {
  border-color: #007bff;
  background: #007bff;
  color: white;
}

.category-btn .count {
  font-size: 12px;
  opacity: 0.7;
}

.icons-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.icon-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.icon-card:hover {
  border-color: #007bff;
  background: #f0f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.icon-card.selected {
  border-color: #007bff;
  background: #007bff;
}

.icon-card i {
  font-size: 32px;
  color: #333;
  transition: color 0.2s;
}

.icon-card.selected i {
  color: white;
}

.icon-label {
  font-size: 12px;
  color: #666;
  text-align: center;
  word-break: break-word;
  font-family: 'Courier New', monospace;
}

.icon-card.selected .icon-label {
  color: white;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 20px;
  color: #999;
}

.empty-state i {
  font-size: 64px;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

.stats-info {
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
  color: #666;
  font-size: 14px;
}
</style>
